import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabase';
import { DELIVERY_FEE, FREE_DELIVERY_THRESHOLD, TAX_RATE } from '../../lib/pricing';

const STATUS_STEPS = [
  { id: 'pending',          label: 'Request Received',  icon: 'Clock' },
  { id: 'paid',             label: 'Payment Confirmed', icon: 'CheckCircle' },
  { id: 'preparing',        label: 'Preparing Order',   icon: 'Coffee' },
  { id: 'out_for_delivery', label: 'Out for Delivery',  icon: 'Truck' },
  { id: 'delivered',        label: 'Hand Delivered',    icon: 'Heart' },
];

const ROW_ONE = STATUS_STEPS.slice(0, 3);
const ROW_TWO = STATUS_STEPS.slice(3);

function ProgressStep({ step, idx, currentStatusIdx, isLastInRow }) {
  const globalIdx = STATUS_STEPS.findIndex(s => s.id === step.id);
  const isCompleted = globalIdx < currentStatusIdx;
  const isCurrent   = globalIdx === currentStatusIdx;
  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      <div className="relative w-full flex items-center justify-center">
        {idx > 0 && (
          <div className={`absolute right-1/2 top-1/2 -translate-y-1/2 h-0.5 w-1/2 ${isCompleted || isCurrent ? 'bg-blush-200' : 'bg-sage-100'}`} />
        )}
        {!isLastInRow && (
          <div className={`absolute left-1/2 top-1/2 -translate-y-1/2 h-0.5 w-1/2 ${isCompleted ? 'bg-blush-200' : 'bg-sage-100'}`} />
        )}
        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
          isCompleted ? 'bg-sage-100 border-sage-300 text-sage-600'
          : isCurrent ? 'bg-blush-300 border-blush-300 text-white shadow-glow scale-110'
          : 'bg-white border-sage-100 text-sage-300'
        }`}>
          <SafeIcon name={step.icon} className="text-base" />
        </div>
      </div>
      <span className={`text-[9px] font-bold uppercase tracking-wide text-center leading-tight px-1 ${isCurrent ? 'text-blush-500' : 'text-sage-400'}`}>
        {step.label}
      </span>
    </div>
  );
}


export default function OrderDetailView({ order, onBack, onUpdate }) {
  const item   = order.order_items?.[0] || {};
  const photos = order.order_photos || [];
  const currentStatusIdx = STATUS_STEPS.findIndex(s => s.id === order.status);

  const [addons, setAddons] = useState([]);

  useEffect(() => {
    try {
      const parsed = item.addons ? JSON.parse(item.addons) : [];
      setAddons(parsed);
    } catch { setAddons([]); }
  }, [item.addons]);

  // Totals
  const bouquetPrice   = item.price || 0;
  const addonsTotal    = addons.reduce((sum, a) => sum + (a.price || 0), 0);
  const subtotal       = bouquetPrice + addonsTotal;
  const taxAmount      = Math.round(subtotal * TAX_RATE * 100) / 100;
  const deliveryCharge = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const storedTotal    = order.stripe_amount_cents
    ? (order.stripe_amount_cents / 100)
    : (subtotal + taxAmount + deliveryCharge);

  useEffect(() => {
    const channel = supabase
      .channel(`order-detail-${order.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders',       filter: `id=eq.${order.id}` },        () => { if (onUpdate) onUpdate(); })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'order_photos', filter: `order_id=eq.${order.id}` }, () => { if (onUpdate) onUpdate(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [order.id, onUpdate]);

  const handleContactUs = () => {
    window.location.href = `mailto:orders@phoenixkabob.com?subject=Order Question - #${order.id.substring(0, 8)}`;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
      <button onClick={onBack} className="btn-secondary mb-8">
        <SafeIcon name="ArrowLeft" /> Back to My Orders
      </button>

      <div className="glass-panel overflow-hidden mb-8">
        {/* ── Header ── */}
        <div className="p-6 md:p-10 border-b border-sage-100 bg-sage-50/30">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <p className="text-xs font-bold text-blush-400 uppercase tracking-widest mb-2">Order #{order.id.substring(0, 8)}</p>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal">{item.product_name || 'Custom Arrangement'}</h2>
            </div>
            {order.payment_link && order.status === 'payment_sent' && (
              <a href={order.payment_link} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-glow">
                Complete Payment <SafeIcon name="ExternalLink" />
              </a>
            )}
          </div>
        </div>

        <div className="p-6 md:p-10 space-y-12">

          {/* ── Progress ── */}
          <div>
            <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-6 md:mb-10">Order Progress</h3>
            <div className="md:hidden space-y-6">
              <div className="flex items-start">
                {ROW_ONE.map((step, idx) => (
                  <ProgressStep key={step.id} step={step} idx={idx} currentStatusIdx={currentStatusIdx} isLastInRow={idx === ROW_ONE.length - 1} />
                ))}
              </div>
              <div className="flex items-start">
                {ROW_TWO.map((step, idx) => (
                  <ProgressStep key={step.id} step={step} idx={idx} currentStatusIdx={currentStatusIdx} isLastInRow={idx === ROW_TWO.length - 1} />
                ))}
                <div className="flex-1" />
              </div>
            </div>
            <div className="hidden md:flex justify-between relative">
              <div className="absolute top-5 left-6 right-6 h-0.5 bg-sage-100" />
              {STATUS_STEPS.map((step, idx) => {
                const isCompleted = idx < currentStatusIdx;
                const isCurrent   = idx === currentStatusIdx;
                return (
                  <div key={step.id} className="flex flex-col items-center gap-3 w-20 relative z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                      isCompleted ? 'bg-sage-100 border-sage-300 text-sage-600'
                      : isCurrent ? 'bg-blush-300 border-blush-300 text-white shadow-glow scale-110'
                      : 'bg-white border-sage-100 text-sage-300'
                    }`}>
                      <SafeIcon name={step.icon} className="text-lg" />
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider text-center leading-tight ${isCurrent ? 'text-blush-500' : 'text-sage-400'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Kabob Order ── */}
          <div className="space-y-4">
            <h4 className="font-serif text-xl border-b border-sage-100 pb-2">Kabob Order</h4>
            <div className="bg-warmwhite rounded-[2rem] border border-sage-100 p-6 md:p-8 flex flex-col sm:flex-row gap-8 items-center sm:items-start">
              {/* Product photo */}
              <div className="w-48 md:w-56 shrink-0 shadow-glow ring-4 ring-white rounded-[2.5rem] overflow-hidden aspect-square bg-sage-50">
                {(photos[0]?.photo_url || item.product_image_url) ? (
                  <img
                    src={photos[0]?.photo_url || item.product_image_url}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl">🥙</div>
                )}
              </div>
              {/* Details alongside */}
              <div className="flex-1 space-y-5">
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest mb-1">Item</p>
                  <p className="font-serif text-2xl text-charcoal leading-tight">{item.product_name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Add-ons ── */}
          {addons.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-serif text-xl border-b border-sage-100 pb-2">Addon</h4>
              <div className="grid grid-cols-3 gap-3">
                {addons.map(addon => (
                  <div key={addon.id} className="bg-warmwhite rounded-2xl p-3 border border-sage-100 flex flex-col items-center gap-2 text-center">
                    {addon.image && (
                      <div className="w-full aspect-square rounded-xl overflow-hidden bg-white flex items-center justify-center p-2">
                        <img src={addon.image} alt={addon.name} className="w-full h-full object-contain" />
                      </div>
                    )}
                    <span className="text-xs font-medium text-charcoal leading-tight">{addon.name}</span>
                    <span className="text-xs font-bold text-blush-400">${addon.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Order total + Delivery side by side ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Order total */}
            <div className="space-y-4">
              <h4 className="font-serif text-xl border-b border-sage-100 pb-2">Order Total</h4>
              <div className="bg-warmwhite rounded-2xl p-5 border border-sage-100 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-sage-400">{item.product_name || 'Item'}</span>
                  <span className="font-medium text-charcoal">${bouquetPrice.toFixed(2)}</span>
                </div>
                {addons.map(addon => (
                  <div key={addon.id} className="flex justify-between text-sm">
                    <span className="text-sage-400">{addon.name}</span>
                    <span className="font-medium text-charcoal">${addon.price.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm">
                  <span className="text-sage-400">Tax (9.1% AZ TPT)</span>
                  <span className="font-medium text-charcoal">${taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-sage-400">Delivery</span>
                  {deliveryCharge === 0
                    ? <span className="font-medium text-green-600">FREE</span>
                    : <span className="font-medium text-charcoal">${deliveryCharge.toFixed(2)}</span>}
                </div>
                <div className="flex justify-between pt-3 border-t border-sage-100">
                  <span className="font-serif text-lg text-charcoal">Total Charged</span>
                  <span className="font-serif text-2xl text-charcoal">${storedTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Delivery details */}
            <div className="space-y-4">
              <h4 className="font-serif text-xl border-b border-sage-100 pb-2">Delivery Details</h4>
              <div className="bg-warmwhite rounded-2xl p-5 border border-sage-100 space-y-4">
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Recipient</p>
                  <p className="text-charcoal font-bold">{order.recipient_name}</p>
                  <p className="text-sm text-sage-500 font-medium">{order.recipient_phone}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Destination</p>
                  <p className="text-charcoal font-bold">{order.delivery_address}</p>
                  <p className="text-charcoal font-bold">{order.delivery_city}, {order.delivery_zip}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Delivery Date</p>
                  <p className="text-charcoal font-bold">
                    {order.delivery_date
                      ? new Date(order.delivery_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact button */}
          {photos.length === 0 && (
            <button onClick={handleContactUs} className="btn-secondary w-full">
              <SafeIcon name="Mail" /> Questions? Contact Us
            </button>
          )}

        </div>
      </div>
    </motion.div>
  );
}
